import React from "react";

export interface DraftRangeProps {
  leftValue: number;
  rightValue: number;
  leftLabel?: string;
  rightLabel?: string;
  className?: string;
}

const DraftRange: React.FC<DraftRangeProps> = ({
  leftValue,
  rightValue,
  leftLabel = "Reach",
  rightLabel = "Steal",
  className = "",
}) => {
  // Format values to show rounded integers
  const formatValue = (value: number) => {
    return Math.round(value);
  };

  // Automatically calculate totalRange as leftValue + rightValue with proper formatting
  const totalRange = formatValue(leftValue + rightValue);

  // Calculate the position percentage for the range bar
  const leftPercentage = Math.max(
    0,
    Math.min(100, (leftValue / totalRange) * 100),
  );
  const rightPercentage = Math.max(
    0,
    Math.min(100, (rightValue / totalRange) * 100),
  );

  // Ensure right value is always greater than left value for visual consistency
  const adjustedLeftPercentage = Math.min(leftPercentage, rightPercentage);
  const adjustedRightPercentage = Math.max(leftPercentage, rightPercentage);

  // Calculate the width of the range bar between the two values
  const rangeWidth = adjustedRightPercentage - adjustedLeftPercentage;

  // Format display values to show rounded integers
  const formatDisplayValue = (value: number) => {
    return Math.round(value).toString();
  };

  return (
    <div className={`mt-[16px] ${className}`}>
      {/* Range visualization container */}
      <div className="relative">
        {/* Background track */}
        <div className="h-[8px] bg-white/10 rounded-full relative overflow-hidden">
          {/* Left section (pink/magenta) */}
          <div
            className="absolute top-0 left-0 h-full bg-[#F11E84] rounded-l-full"
            style={{ width: `${adjustedLeftPercentage}%` }}
          />

          {/* Middle section (gray) - between the two values */}
          <div
            className="absolute top-0 h-full bg-white/10"
            style={{
              left: `${adjustedLeftPercentage}%`,
              width: `${rangeWidth}%`,
            }}
          />

          {/* Right section (green) */}
          <div
            className="absolute top-0 right-0 h-full bg-[#16C265] rounded-r-full"
            style={{ width: `${100 - adjustedRightPercentage}%` }}
          />
        </div>

        {/* Left value indicator */}
        <div
          className="absolute top-[-8px] transform -translate-x-1/2"
          style={{ left: `${adjustedLeftPercentage}%` }}
        >
          <div className="w-[24px] h-[24px] bg-white rounded-full flex items-center justify-center ">
            <span className="text-black text-[10px] font-bold">
              {formatDisplayValue(leftValue)}
            </span>
          </div>

          {/* Left vertical line */}
          <div className="absolute top-[28px] left-1/2 transform  w-[1px] h-[10px] bg-white"></div>

          {/* Left label */}
          <div className="absolute mt-2  left-1/2 transform -translate-x-1/2 whitespace-nowrap">
            <span className="text-white text-[10px] font-bold">
              {leftLabel}
            </span>
          </div>
        </div>

        {/* Right value indicator */}
        <div
          className="absolute top-[-8px] transform -translate-x-1/2"
          style={{ left: `${adjustedRightPercentage}%` }}
        >
          <div className="w-[24px] h-[24px] bg-white rounded-full flex items-center justify-center ">
            <span className="text-black text-[10px] font-bold">
              {formatDisplayValue(rightValue)}
            </span>
          </div>

          {/* Right vertical line */}
          <div className="absolute top-[28px] left-1/2 transform  w-[1px]  h-[10px] bg-white"></div>

          {/* Right label */}
          <div className="absolute  mt-2  left-1/2 transform -translate-x-1/2 whitespace-nowrap">
            <span className="text-white text-[10px] font-bold">
              {rightLabel}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DraftRange;
